import omit from 'lodash/omit';

import * as constants from '../../../redux/constants';
import { getProviderIDs } from '../../../util';
import modifyRankingValue from '../../elements/element/ranking-input/modify-value';
import { getEmailPromptId } from '../../elements/element/social-input/get-email-prompt-id';
import { shouldPromptForEmail } from '../../elements/element/social-input/should-prompt-for-email';
import getElementOtherImage from '../../elements/element-util/get-element-other-image';
import { t } from '../../../../../translations/i18n';

function createEnum(element, ...rest) {
  const optionalValues = [];
  const { allOfTheAboveOption, noneOfTheAboveOption, values } = element.properties;
  if (allOfTheAboveOption) optionalValues.push('all_of_the_above_option');
  if (noneOfTheAboveOption) optionalValues.push('none_of_the_above_option');
  return [...values, ...optionalValues, ...rest];
}

function getProperties(element, isPreview) {
  const {
    maxChars,
    maxSelect,
    minSelect,
    minChars,
    maxValue,
    minValue,
    otherOption,
    otherValue = 'Other',
    required,
    supportedCountryCodes = [],
    values,
  } = element.properties;

  const requiredMessage = t('validation.required');
  const maxLengthMessage = t('validation.maxLength', { count: maxChars });
  const minLengthMessage = t('validation.minLength', { count: minChars });
  const gteMessage = t('validation.gte', { count: minValue });
  const lteMessage = t('validation.lte', { count: maxValue });

  switch (element.type) {
    case constants.ELEMENT_TYPE__DISPLAY_REWARD:
    case constants.ELEMENT_TYPE__HYPERLINK:
    case constants.ELEMENT_TYPE__IMAGE:
    case constants.ELEMENT_TYPE__TEXT:
      return {};

    case constants.ELEMENT_TYPE__BOOLEAN_INPUT:
      return {
        default: required ? undefined : null,
        type: ['boolean', 'null'],
        messages: { required: requiredMessage },
      };

    case constants.ELEMENT_TYPE__CHECKBOX_INPUT: {
      const minItems = Math.max(required ? 1 : 0, minSelect || 0);
      const allowedValues = createEnum(element);
      return {
        default: required ? undefined : [],
        items: otherOption ? undefined : { enum: allowedValues },
        type: 'array',
        maxItems: maxSelect,
        minItems,
        messages: {
          maxItems: t('validation.checkboxMaxItems', { count: maxSelect }),
          minItems: t('validation.checkboxMinItems', { count: minItems }),
          required: requiredMessage,
          items: t('validation.shouldBeAnyOf', { values: allowedValues.join(', ') }),
          type: t('validation.checkboxUncheckOrFill', { otherValue }),
        },
      };
    }

    case constants.ELEMENT_TYPE__DATE_INPUT:
      return {
        default: required ? undefined : null,
        type: ['string', 'null'],
        format: 'date',
        messages: {
          format: t('validation.formatDate'),
          required: requiredMessage,
        },
      };

    case constants.ELEMENT_TYPE__EMAIL_INPUT:
    case constants.ELEMENT_TYPE__EMAIL_VERIFICATION_INPUT:
      return {
        default: required ? undefined : null,
        type: ['string', 'null'],
        format: 'email',
        messages: {
          format: t('validation.emailInvalid'),
          required: requiredMessage,
        },
      };

    case constants.ELEMENT_TYPE__GIFTBIT_REWARD_INPUT:
      return {
        default: required ? undefined : null,
        type: ['string', 'null'],
        messages: { required: t('validation.selectReward') },
      };

    case constants.ELEMENT_TYPE__IMAGE_INPUT: {
      const defaultValue = required ? undefined : null;
      const allowedValues = createEnum(element, defaultValue);
      return {
        default: defaultValue,
        enum: otherOption ? undefined : allowedValues,
        messages: {
          enum: t('validation.shouldBeOneOf', { values: allowedValues.join(', ') }),
          minLength: requiredMessage,
          required: requiredMessage,
        },
        type: ['string', 'null'],
      };
    }

    case constants.ELEMENT_TYPE__IMAGE_UPLOAD_INPUT:
      return {
        default: required ? undefined : null,
        messages: { required: requiredMessage },
        type: ['string', 'null'],
      };

    case constants.ELEMENT_TYPE__IMAGE_MULTI_SELECT_INPUT: {
      const allowedValues = createEnum(element);
      if (otherOption) {
        allowedValues.push(getElementOtherImage(element).otherValue);
      }
      return {
        default: required ? undefined : [],
        items: { enum: allowedValues.length ? allowedValues : undefined },
        minItems: required ? 1 : undefined,
        messages: {
          minItems: requiredMessage,
          required: requiredMessage,
          items: t('validation.shouldBeAnyOf', { values: allowedValues.join(', ') }),
        },
        type: 'array',
      };
    }

    case constants.ELEMENT_TYPE__MEDIA_INPUT:
      return {
        default: required ? undefined : null,
        type: ['number', 'null'],
        minimum: isPreview || !required ? 0 : 0.9,
        messages: {
          minimum: t('validation.playEntireMediaClip'),
          required: requiredMessage,
        },
      };

    case constants.ELEMENT_TYPE__NUMBER_INPUT:
      return {
        default: required ? undefined : null,
        type: ['number', 'null'],
        minimum: minValue,
        maximum: maxValue,
        messages: { minimum: gteMessage, maximum: lteMessage, required: requiredMessage },
      };

    case constants.ELEMENT_TYPE__PHONE_NUMBER_INPUT: {
      const codesString = supportedCountryCodes.length
        ? `(${supportedCountryCodes.join(' or ')})`
        : supportedCountryCodes[0];
      return {
        default: required ? undefined : null,
        type: ['string', 'null'],
        messages: {
          required: requiredMessage,
          type: supportedCountryCodes.length
            ? t('validation.phoneNumberInvalidWithCodes', { codes: codesString })
            : t('validation.phoneNumberInvalid'),
        },
      };
    }

    case constants.ELEMENT_TYPE__RADIO_INPUT: {
      const defaultValue = required ? undefined : null;
      const allowedValues = createEnum(element, defaultValue);
      return {
        default: defaultValue,
        type: ['string', 'null'],
        enum: otherOption ? undefined : [...allowedValues],
        minLength: required ? 1 : undefined,
        messages: {
          enum: t('validation.shouldBeOneOf', { values: allowedValues.join(', ') }),
          minLength: requiredMessage,
          required: requiredMessage,
        },
      };
    }

    case constants.ELEMENT_TYPE__RANKING_IMAGE_INPUT:
      return {
        default: required ? undefined : null,
        items: { enum: required ? values : [...values, null] },
        messages: {
          enum: t('validation.shouldBeOneOf', { values: values.join(', ') }),
          minLength: requiredMessage,
          required: t('validation.rankingImageRequired'),
        },
        type: ['array', 'null'],
      };

    case constants.ELEMENT_TYPE__RANKING_INPUT: {
      const defaultValue = required ? undefined : null;
      return {
        default: defaultValue,
        items: { enum: element.properties.options.map(modifyRankingValue) },
        type: ['array', 'null'],
        minLength: required ? 1 : undefined,
        messages: {
          minLength: requiredMessage,
          required: t('validation.rankingRequired'),
          type: t('validation.rankingTypeInvalid'),
        },
      };
    }

    case constants.ELEMENT_TYPE__SLIDE_INPUT:
      return {
        default: required ? undefined : null,
        type: ['number', 'null'],
        format: 'floatFormat',
        minimum: minValue,
        maximum: maxValue,
        messages: {
          minimum: gteMessage,
          maximum: lteMessage,
          required: t('validation.slideHandleRequired'),
        },
      };

    case constants.ELEMENT_TYPE__SOCIAL_INPUT: {
      const allowedProviders = getProviderIDs();
      return {
        default: required ? undefined : null,
        enum: [...allowedProviders, null],
        type: ['string', 'null'],
        messages: {
          enum: t('validation.shouldBeOneOf', { values: allowedProviders.join(', ') }),
          required: t('validation.socialRequired'),
        },
      };
    }

    case constants.ELEMENT_TYPE__TAROT_INPUT:
      return {
        default: required ? undefined : null,
        type: ['string', 'null'],
        messages: { required: t('validation.tarotRequired') },
      };

    case constants.ELEMENT_TYPE__TEXTAREA:
      return {
        default: required ? undefined : null,
        type: ['string', 'null'],
        maxLength: maxChars,
        minLength: minChars,
        messages: {
          maxLength: maxLengthMessage,
          minLength: minLengthMessage,
          required: requiredMessage,
        },
      };

    case constants.ELEMENT_TYPE__TEXT_INPUT:
      return {
        default: required ? undefined : null,
        type: ['string', 'null'],
        maxLength: maxChars,
        minLength: minChars,
        messages: {
          maxLength: maxLengthMessage,
          minLength: minLengthMessage,
          required: requiredMessage,
        },
      };

    case constants.ELEMENT_TYPE__ZIP_INPUT:
      return {
        default: required ? undefined : null,
        type: ['string', 'null'],
        minLength: 4,
        maxLength: 10,
        messages: {
          maxLength: t('validation.maxLength', { count: 10 }),
          minLength: t('validation.minLength', { count: 4 }),
          required: requiredMessage,
        },
      };

    default:
      throw new Error(`Unknown element type: ${element.type}`);
  }
}

export default ({ elements, isPreview, participant, isTestMode }) => {
  const requiredEmailPromptInputs = elements
    .filter((element) => shouldPromptForEmail(element, participant))
    .map(({ id }) => getEmailPromptId(id));

  const schema = elements.reduce(
    (prevSchema, element) => {
      const nextSchema = prevSchema;
      nextSchema.properties[element.id] = getProperties(element, isPreview);
      if (!isTestMode && element.properties.required) {
        nextSchema.required.push(element.id);
      }
      return nextSchema;
    },
    {
      type: 'object',
      properties: {
        formAction: { type: 'string' },
        promptForEmail: { type: 'string' },
      },
      required: requiredEmailPromptInputs,
    },
  );

  return schema.required.length ? schema : omit(schema, 'required');
};

/**
 * @see docs/worklog/maintenance/20093-investigate-facebook-auth-errors/rfc-frontend.md
 */
